// Phase SIKA-2 — useSikaGoal hook.
//
// Returns full SIKA goal view-model: goal row + weekly cycles +
// milestones + chat messages. Realtime subscribes on own user rows
// using useId() per-mount channel name (mandatory pattern per
// useSikaChat / useRolloverChain conventions).
//
// Auth-only (anon returns empty state). Service-role writes happen
// via the Goal Agent / recalibration RPC — this hook is read-only.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// SIKA-1 / SIKA-2 added tables/columns that aren't yet in db/types.ts.
// We use untyped queries (`from(name as never)`) and shape the rows
// manually. Once types are regenerated this hook can switch back to
// the strict Database['public']['Tables'] inference.
export interface SikaGoalRow {
  id: string;
  user_id: string;
  title: string;
  target_amount: number;
  current_amount: number;
  banked_amount: number;
  mode: "simple" | "chain";
  linked_chain_id: string | null;
  status: string;
  deadline: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface SikaCycleRow {
  cycle_id: string;
  goal_id: string;
  cycle_number: number;
  window_label: "midweek" | "weekend";
  dynamic_target: number;
  dynamic_target_rationale: Record<string, unknown> | null;
  mapped_tier: "2x" | "3x" | "4x" | "5x";
  is_urgent: boolean;
  status: string;
  outcome: string | null;
  stake_amount: number | null;
  return_amount: number | null;
  recalibrated: boolean;
  recalibration_reason: string | null;
  settled_at: string | null;
  published_at: string | null;
  created_at: string;
  /** SIKA-4b: board consensus + future cycle metadata. Always an object (defaults '{}'). */
  metadata?: {
    board_consensus?: {
      agreement_count: number;
      elevated: boolean;
      agreeing_agents: number[];
    };
  } | null;
}

export interface SikaCycleLegRow {
  leg_id: string;
  cycle_id: string;
  match_name: string;
  league: string | null;
  market: string | null;
  pick: string | null;
  odds: number;
  confidence_score: number | null;
  goal_reasoning: string;
  leg_order: number;
  outcome: string | null;
}

export interface SikaMilestoneRow {
  id: string;
  goal_id: string;
  milestone_type: string;
  milestone_pct: number;
  milestone_amount_ghc: number;
  payload: Record<string, unknown>;
  triggered_at: string;
  acknowledged_at: string | null;
}

export interface SikaChatMessageRow {
  id: string;
  goal_id: string | null;
  user_id: string;
  role: string;
  content: string;
  template_id: string | null;
  is_proactive: boolean;
  trigger_event: string | null;
  created_at: string;
}

export interface SikaChainProtectionWeekRow {
  week_number: number;
  amount_from_this_week: number;
  cumulative_protected_amount: number;
}

export interface SikaChainProtectionSummary {
  cumulative: number;
  weeks: SikaChainProtectionWeekRow[];
}

export interface UseSikaGoalState {
  goal: SikaGoalRow | null;
  cycles: SikaCycleRow[];
  legsByCycle: Record<string, SikaCycleLegRow[]>;
  milestones: SikaMilestoneRow[];
  chatMessages: SikaChatMessageRow[];
  chainProtection: SikaChainProtectionSummary | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const EMPTY_STATE: Omit<UseSikaGoalState, "loading" | "error" | "refetch"> = {
  goal: null,
  cycles: [],
  legsByCycle: {},
  milestones: [],
  chatMessages: [],
  chainProtection: null,
};

export function useSikaGoal(goalIdOpt?: string | null): UseSikaGoalState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const instanceId = useId();

  const [state, setState] = useState<Omit<UseSikaGoalState, "loading" | "error" | "refetch">>(
    EMPTY_STATE,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!user) {
      setState(EMPTY_STATE);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);

    // Resolve goalId — explicit prop, or auto-select first non-terminal goal.
    // SIKA-5c/B: widened from status='active' only to include 'in_progress'
    // and any other non-terminal status, matching useSikaGoals.activeGoals
    // filter in SikaGoalPage. Otherwise newly-created goals (in_progress
    // before first cycle settles) would render the empty Panel 1 state.
    let goalId = goalIdOpt ?? null;
    if (!goalId) {
      const { data: firstActive, error: firstErr } = await supabase
        .from("sika_goals")
        .select("id,status")
        .eq("user_id", user.id)
        .not("status", "in", "(completed,archived,cancelled)")
        .order("created_at", { ascending: false })
        .limit(1);
      if (firstErr) {
        setError(firstErr.message);
        setLoading(false);
        return;
      }
      goalId = firstActive?.[0]?.id ?? null;
    }

    if (!goalId) {
      setState(EMPTY_STATE);
      setLoading(false);
      return;
    }

    // Load goal row
    const { data: goalData, error: goalErr } = await supabase
      .from("sika_goals")
      .select("*")
      .eq("id", goalId)
      .eq("user_id", user.id)
      .limit(1);
    if (goalErr) {
      setError(goalErr.message);
      setLoading(false);
      return;
    }
    const goalRow = (goalData?.[0] ?? null) as unknown as SikaGoalRow | null;
    if (!goalRow) {
      setState(EMPTY_STATE);
      setLoading(false);
      return;
    }

    // Load cycles, legs, milestones, chat in parallel
    const [cyclesRes, milestonesRes, chatRes] = await Promise.all([
      supabase
        .from("sika_weekly_cycles" as never)
        .select("*")
        .eq("goal_id", goalId)
        .order("cycle_number", { ascending: true }),
      supabase
        .from("sika_milestones")
        .select("*")
        .eq("goal_id", goalId)
        .order("triggered_at", { ascending: false }),
      supabase
        .from("sika_messages")
        .select("*")
        .eq("goal_id", goalId)
        .order("created_at", { ascending: true })
        .limit(200),
    ]);

    const cycles = (cyclesRes.data ?? []) as unknown as SikaCycleRow[];
    const milestones = (milestonesRes.data ?? []) as unknown as SikaMilestoneRow[];
    const chatMessages = (chatRes.data ?? []) as unknown as SikaChatMessageRow[];

    let legsByCycle: Record<string, SikaCycleLegRow[]> = {};
    const cycleIds = cycles.map((c) => c.cycle_id);
    if (cycleIds.length > 0) {
      const { data: legsData } = await supabase
        .from("sika_cycle_legs" as never)
        .select("*")
        .in("cycle_id", cycleIds)
        .order("leg_order", { ascending: true });
      const legs = (legsData ?? []) as unknown as SikaCycleLegRow[];
      legsByCycle = legs.reduce<Record<string, SikaCycleLegRow[]>>((acc, leg) => {
        (acc[leg.cycle_id] ??= []).push(leg);
        return acc;
      }, {});
    }

    // Chain Mode read-through — read per-week protection rows for the
    // linked chain so Panel 4 can render the cumulative total + weekly
    // breakdown. Untyped (SIKA-3 view not yet in generated types).
    let chainProtection: SikaChainProtectionSummary | null = null;
    if (goalRow.mode === "chain" && goalRow.linked_chain_id) {
      const { data: protData } = await supabase
        .from("chain_protected_amounts" as never)
        .select("week_number,amount_from_this_week,cumulative_protected_amount")
        .eq("chain_id", goalRow.linked_chain_id)
        .order("week_number", { ascending: true });
      const weeks = (protData ?? []) as unknown as SikaChainProtectionWeekRow[];
      const cumulative =
        weeks.length > 0
          ? Math.max(
              ...weeks.map((w) => Number(w.cumulative_protected_amount) || 0),
            )
          : 0;
      chainProtection = { cumulative, weeks };
    }

    setState({
      goal: goalRow,
      cycles,
      legsByCycle,
      milestones,
      chatMessages,
      chainProtection,
    });
    setLoading(false);
  }, [supabase, user, goalIdOpt]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  // Realtime — own-row subscription on the goal's cycles + messages.
  // Per-mount channel name avoids cross-tab collision (mandatory pattern).
  useEffect(() => {
    if (!user || !state.goal) return;
    const channelName = `sika-goal:${instanceId}:${state.goal.id}`;
    const ch = supabase
      .channel(channelName)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "sika_weekly_cycles",
          filter: `goal_id=eq.${state.goal.id}`,
        },
        () => {
          void refetch();
        },
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "sika_messages",
          filter: `goal_id=eq.${state.goal.id}`,
        },
        () => {
          void refetch();
        },
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [supabase, user, state.goal, instanceId, refetch]);

  return useMemo(
    () => ({ ...state, loading, error, refetch }),
    [state, loading, error, refetch],
  );
}
